import { useEffect } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { companyHref } from "@/lib/entityRoutes";
import ClientRecord from "./company/ClientRecord";
import type { ClientData } from "./company/client/clientTypes";

/**
 * /clients/:id. Loads the client and hands it to ClientRecord, which holds the
 * page. A company that is no longer a Client (moved since the link was made)
 * forwards to its own page, as CompanyDetail forwards a Client to here.
 */
export default function ClientDetail() {
  const [, params] = useRoute("/clients/:id");
  const companyId = params?.id;
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const { data: company, isLoading, isError, error } = useQuery<ClientData>({
    queryKey: ["company", companyId],
    queryFn: async () => {
      const res = await fetch(`/api/companies/${companyId}`, { credentials: "include" });
      // Distinguish "this record is gone" from "the request failed", so the
      // error screen can tell the user which one happened.
      if (res.status === 404) throw new Error("NOT_FOUND");
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: !!companyId,
    // A deleted record will never appear on retry, and retrying just delays
    // the message. Anything else is worth one retry.
    retry: (count, err) => (err as Error)?.message !== "NOT_FOUND" && count < 1,
  });

  const isGone = (error as Error | null)?.message === "NOT_FOUND";
  const target = company ? companyHref(company.id, company.category) : null;
  const elsewhere = target && !target.startsWith("/clients/") ? target : null;

  // A 404 here means the clients list that linked us is stale: someone deleted
  // this client while this browser still had the list cached. Drop the cached
  // list so the phantom row disappears instead of being clickable forever.
  useEffect(() => {
    if (isGone) queryClient.invalidateQueries({ queryKey: ["clients"] });
  }, [isGone]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (elsewhere) setLocation(elsewhere, { replace: true });
  }, [elsewhere]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || elsewhere) {
    return (
      <AppLayout title="Client">
        <div className="flex min-h-[400px] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  if (isError || !company) {
    return (
      <AppLayout title="Client">
        <div className="p-6 py-12 text-center">
          <p className="text-muted-foreground">
            {isGone
              ? "This client no longer exists. It was most likely deleted, and the list you came from was showing a cached copy."
              : "Could not load this client. Check your connection and try again."}
          </p>
          <Link href="/clients"><Button variant="outline" className="mt-4">Back to Clients</Button></Link>
        </div>
      </AppLayout>
    );
  }

  return <ClientRecord key={company.id} company={company} />;
}
